/**
 * How library pieces move between pages without knowing the router. The app wires react-router into `NavProvider`;
 * the /__ui board wires a local one, so the AppShell demo can switch sections without leaving the board. Without a
 * provider it falls back to the browser's own history.
 */
import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'

export interface Where {
  pathname: string
  search: string
}

export interface Nav {
  where: Where
  navigate: (to: string, opts?: { replace?: boolean }) => void
}

const NavContext = createContext<Nav | null>(null)

export function NavProvider({ value, children }: { value: Nav; children: ReactNode }) {
  return <NavContext.Provider value={value}>{children}</NavContext.Provider>
}

const browserWhere = (): Where =>
  typeof location === 'undefined' ? { pathname: '/', search: '' } : { pathname: location.pathname, search: location.search }

/** The browser's history, for a library piece rendered with no provider. */
function useBrowserNav(): Nav {
  const [where, setWhere] = useState(browserWhere)
  useEffect(() => {
    const on = () => setWhere(browserWhere())
    addEventListener('popstate', on)
    return () => removeEventListener('popstate', on)
  }, [])
  return {
    where,
    navigate: (to, opts) => {
      history[opts?.replace ? 'replaceState' : 'pushState'](null, '', to)
      setWhere(browserWhere())
    },
  }
}

export function useNav(): Nav {
  const provided = useContext(NavContext)
  const fallback = useBrowserNav()
  return provided ?? fallback
}

/** A link that moves through `useNav`, so a click stays in the single-page app. Middle-click and ⌘-click still open a tab. */
export function Link({ to, children, ...rest }: { to: string; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className' | 'style'>) {
  const nav = useNav()
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    rest.onClick?.(e)
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    nav.navigate(to)
  }
  return (
    <a {...rest} href={to} onClick={onClick}>
      {children}
    </a>
  )
}
